import { collection, query, orderBy, limit, getDocs, Timestamp, doc, setDoc, deleteDoc, runTransaction, where, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { Appointment, SERVICE_LABELS } from '../types/appointment';
import { DisabledSlot, formatSlotId } from '../types/availability';
import { formatDateLA, formatTimeLA } from '../utils/timezone';

export interface BookingWithId extends Appointment {
  id: string;
}

export const adminService = {
  /**
   * Fetch all bookings from Firestore
   * @param limitCount - Maximum number of bookings to fetch (default: 50)
   * @param includeCompleted - Whether to include completed/past appointments (default: true)
   */
  async getAllBookings(limitCount: number = 50, includeCompleted: boolean = true): Promise<BookingWithId[]> {
    try {
      const appointmentsRef = collection(db, 'appointments');
      
      // Try with orderBy first, fall back to simple query if index doesn't exist
      let q;
      try {
        q = query(
          appointmentsRef,
          orderBy('scheduledDateTime', 'asc'),
          limit(limitCount)
        );
        const querySnapshot = await getDocs(q);
        
        const bookings: BookingWithId[] = [];
        querySnapshot.forEach((doc) => {
          bookings.push({
            id: doc.id,
            ...doc.data() as Appointment
          });
        });

        return this.filterBookings(bookings, includeCompleted);
      } catch (indexError: any) {
        // If orderBy fails due to missing index, try without ordering
        console.warn('OrderBy failed, fetching without ordering:', indexError);
        
        q = query(appointmentsRef, limit(limitCount));
        const querySnapshot = await getDocs(q);
        
        const bookings: BookingWithId[] = [];
        querySnapshot.forEach((doc) => {
          bookings.push({
            id: doc.id,
            ...doc.data() as Appointment
          });
        });

        // Sort in memory by scheduled appointment time (earliest first)
        bookings.sort((a, b) => {
          const aTime = a.scheduledDateTime?.toMillis() || 0;
          const bTime = b.scheduledDateTime?.toMillis() || 0;
          return aTime - bTime; // Ascending order (earliest appointments first)
        });

        return this.filterBookings(bookings, includeCompleted);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  },

  /**
   * Filter bookings based on date and completion status
   */
  filterBookings(bookings: BookingWithId[], includeCompleted: boolean): BookingWithId[] {
    if (includeCompleted) {
      return bookings;
    }

    const now = new Date();
    return bookings.filter(booking => {
      // Include if status is not completed/cancelled, or if appointment is in the future
      if (['pending', 'confirmed'].includes(booking.status)) {
        return true;
      }
      
      // For completed/cancelled, only include if the appointment date is in the future
      const appointmentDate = booking.scheduledDateTime.toDate();
      return appointmentDate >= now;
    });
  },

  /**
   * Get upcoming bookings only (excludes past completed appointments)
   */
  async getUpcomingBookings(limitCount: number = 50): Promise<BookingWithId[]> {
    return this.getAllBookings(limitCount, false);
  },

  /**
   * Format timestamp to readable date string
   */
  formatDate(timestamp: Timestamp): string {
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  /**
   * Get service label from service type
   */
  getServiceLabel(serviceType: string): string {
    return SERVICE_LABELS[serviceType as keyof typeof SERVICE_LABELS] || serviceType;
  },

  /**
   * Disable a time slot - Uses transaction to prevent race conditions
   */
  async disableSlot(date: string, time: string, reason?: string): Promise<void> {
    const slotId = formatSlotId(date, time);
    const slotRef = doc(db, 'disabledSlots', slotId);

    try {
      await runTransaction(db, async (transaction) => {
        // Check if slot already exists
        const slotDoc = await transaction.get(slotRef);
        
        if (slotDoc.exists()) {
          throw new Error('Slot is already disabled');
        }

        // Check if there's an existing appointment for this slot
        const appointmentsRef = collection(db, 'appointments');
        const startOfDay = new Date(date + 'T00:00:00-08:00'); // LA timezone
        const endOfDay = new Date(date + 'T23:59:59-08:00');
        
        const q = query(
          appointmentsRef,
          where('scheduledDateTime', '>=', Timestamp.fromDate(startOfDay)),
          where('scheduledDateTime', '<=', Timestamp.fromDate(endOfDay)),
          where('status', 'in', ['confirmed', 'pending'])
        );

        const appointmentSnapshot = await getDocs(q);
        const hasConflict = appointmentSnapshot.docs.some(doc => {
          const data = doc.data();
          const appointmentDate = data.scheduledDateTime.toDate();
          const appointmentTime = this.formatTime(appointmentDate);
          return appointmentTime === time;
        });

        if (hasConflict) {
          throw new Error('Cannot disable slot: There is an existing appointment');
        }

        // Create the disabled slot
        const disabledSlot: DisabledSlot = {
          date,
          time,
          disabledAt: Timestamp.now(),
          disabledBy: 'admin',
          reason: reason || 'Manually disabled by admin'
        };

        transaction.set(slotRef, disabledSlot);
      });
    } catch (error) {
      console.error('Error disabling slot:', error);
      throw error;
    }
  },

  /**
   * Enable a previously disabled time slot
   */
  async enableSlot(date: string, time: string): Promise<void> {
    const slotId = formatSlotId(date, time);
    const slotRef = doc(db, 'disabledSlots', slotId);

    try {
      await deleteDoc(slotRef);
    } catch (error) {
      console.error('Error enabling slot:', error);
      throw error;
    }
  },

  /**
   * Get all disabled slots for a specific date
   */
  async getDisabledSlots(date: string): Promise<DisabledSlot[]> {
    try {
      const disabledSlotsRef = collection(db, 'disabledSlots');
      const q = query(disabledSlotsRef, where('date', '==', date));
      
      const querySnapshot = await getDocs(q);
      const slots: DisabledSlot[] = [];
      
      querySnapshot.forEach(doc => {
        slots.push(doc.data() as DisabledSlot);
      });

      return slots;
    } catch (error) {
      console.error('Error fetching disabled slots:', error);
      return [];
    }
  },

  /**
   * Format Date to time string for display
   */
  formatTime(date: Date): string {
    const hour = date.getHours();
    const minute = date.getMinutes();
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute.toString().padStart(2, '0');
    
    return `${displayHour}:${displayMinute} ${period}`;
  },

  /**
   * Cancel an appointment and free up the time slot
   * This removes both the appointment and the corresponding disabled slot
   */
  async cancelAppointment(appointmentId: string): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        // Get the appointment first
        const appointmentRef = doc(db, 'appointments', appointmentId);
        const appointmentDoc = await transaction.get(appointmentRef);
        
        if (!appointmentDoc.exists()) {
          throw new Error('Appointment not found');
        }

        const appointmentData = appointmentDoc.data() as Appointment;
        
        // Update appointment status to cancelled
        transaction.update(appointmentRef, {
          status: 'cancelled',
          updatedAt: Timestamp.now()
        });

        // Remove the corresponding disabled slot
        const date = formatDateLA(appointmentData.scheduledDateTime.toDate());
        const time = formatTimeLA(appointmentData.scheduledDateTime.toDate());
        
        // Find and remove the disabled slot for this appointment
        const disabledSlotsRef = collection(db, 'disabledSlots');
        const disabledSlotsQuery = query(
          disabledSlotsRef,
          where('date', '==', date),
          where('time', '==', time),
          where('appointmentId', '==', appointmentId)
        );
        
        const disabledSlotsSnapshot = await getDocs(disabledSlotsQuery);
        disabledSlotsSnapshot.forEach(doc => {
          transaction.delete(doc.ref);
        });
      });

      console.log('Appointment cancelled and slot freed:', appointmentId);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      throw error;
    }
  },

  /**
   * Delete an appointment completely and free up the time slot
   * This removes both the appointment and the corresponding disabled slot
   */
  async deleteAppointment(appointmentId: string): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        // Get the appointment first
        const appointmentRef = doc(db, 'appointments', appointmentId);
        const appointmentDoc = await transaction.get(appointmentRef);
        
        if (!appointmentDoc.exists()) {
          throw new Error('Appointment not found');
        }

        const appointmentData = appointmentDoc.data() as Appointment;
        
        // Delete the appointment
        transaction.delete(appointmentRef);

        // Remove the corresponding disabled slot
        const date = formatDateLA(appointmentData.scheduledDateTime.toDate());
        const time = formatTimeLA(appointmentData.scheduledDateTime.toDate());
        
        // Find and remove the disabled slot for this appointment
        const disabledSlotsRef = collection(db, 'disabledSlots');
        const disabledSlotsQuery = query(
          disabledSlotsRef,
          where('date', '==', date),
          where('time', '==', time),
          where('appointmentId', '==', appointmentId)
        );
        
        const disabledSlotsSnapshot = await getDocs(disabledSlotsQuery);
        disabledSlotsSnapshot.forEach(doc => {
          transaction.delete(doc.ref);
        });
      });

      console.log('Appointment deleted and slot freed:', appointmentId);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  }
};
